<script lang="ts">
	import { format } from 'svelte-i18n';
	import { Button, ButtonV2, Container, Modal, Text, toast } from '@Shelly/shelly-design-system';
	import { resetEditUpdateStore, update } from 'src/stores/ui/edit_lists';
	import { home_data } from 'src/stores/account/user';
	import { capFirstChar } from 'src/helpers/utilities';
	import BatchApplianceType from './BatchApplianceType.svelte';
	import BatchToggler from './BatchToggler.svelte';
	import BatchText from './BatchText.svelte';
	import { postReq } from 'src/services/http';
	import type { Device } from 'src/types/Device';
	import { devices } from 'src/stores/lists/devices';
	import {
		castUsageStringToRelayUsageParam,
		filterPowerMeasuringDevices,
	} from 'src/helpers/device_settings_helpers';
	import PinUnlock from '../PinUnlock.svelte';
	import { NotifType } from 'src/types/DeviceNotifPermissions';
	import { eraseOrCancelAIModelBulk } from '../DevicesSettings/ApplicationSettings/AIModel/ai_helper';
	import {
		getHomeEnergyDesignationForDevice,
		isDeviceEnergyDesignated,
	} from 'src/components/Settings/HomeManagement/shared/solar-setup-helpers';
	import { removeDeviceFromHomeEnergyDesignations } from 'src/components/Settings/HomeManagement/shared/solar-setup-persist';

	export let checked: boolean;
	export let showModal: boolean;
	export let roomConsumption: boolean;
	export let accountConsumption: boolean;
	export let areFiltersDisabled: boolean;

	let component: any = BatchApplianceType;
	let variable: any;
	let successMessage = '';
	let errorMessage = $format('setting_err_toast', {
		default: 'Failed to apply settings.',
	});
	let showPinCode = false;
	let showClearDesignationModal = false;
	let skipDesignationPrompt = false;
	let waitForResponse = false;

	switch ($update.type) {
		case 'exclude_event_log':
			component = BatchToggler;
			variable = checked;
			successMessage = $format('event_log_scc_toast', {
				default: 'Event log exclusion settings changed.',
			});
			break;
		case 'consumption_calculation':
			component = BatchToggler;
			variable = { roomConsumption, accountConsumption };
			successMessage = $format('toast_success_consumption_calculation_msg', {
				default:
					'Devices have successfully changed their total room and account consumption settings.',
			});
			break;
		case 'exclude_forgotten_lights':
			component = BatchToggler;
			variable = checked;
			successMessage = $format('toast_succ_exclude_ai_forgotten', {
				default: '"Forgotten lights" notifications and e-mails settings changed.',
			});
			break;
		case 'ai_device_failure':
			if ($update.subType === 'manage_ai_notifications') {
				component = BatchToggler;
				variable = checked;
				successMessage = $format('toast_succ_exclude_ai_failure', {
					default: '"Failure detection" notifications and e-mails settings changed.',
				});
			} else {
				component = BatchText;
				variable = checked;
				successMessage =
					$update.subType === 'enable_ai_model'
						? $format('toast_succ_enable_ai_model', {
								default: 'Successfully enabled AI model for this devices.',
							})
						: $format('toast_succ_disable_ai_model', {
								default: 'Successfully disabled AI model for this devices.',
							});
			}
			break;
		case 'measure_peak_power':
			component = BatchToggler;
			variable = checked;
			successMessage = variable
				? $format('toast_success_enable_peak_measurement', {
						default: 'Devices have successfully enabled "Peak Power" measurements.',
					})
				: $format('toast_success_disable_peak_measurement', {
						default: 'Devices have successfully disabled "Peak Power" measurements.',
					});
			break;
		case 'pin_lock':
			component = BatchToggler;
			variable = checked;
			successMessage = variable
				? $format('enable_pin_lock_scc_toast', {
						default: 'PIN lock was enabled.',
					})
				: $format('disable_pin_lock_scc_toast', {
						default: 'PIN lock was disabled.',
					});
			break;
	}

	const getToggleVariable = (e) => {
		variable = e.detail;
		switch ($update.type) {
			case 'appliance_type':
				successMessage = $format('appliance_type_scc_toast', {
					values: { value: variable.title },
					default: `Appliance type was changed to ${variable.title}.`,
				});
				break;
			case 'consumption_calculation':
				variable = {
					roomConsumption: e.detail.roomConsumption,
					accountConsumption: e.detail.accountConsumption,
				};
				break;
			case 'measure_peak_power':
				successMessage = variable
					? $format('toast_success_enable_peak_measurement', {
							default: 'Devices have successfully enabled "Peak Power" measurements.',
						})
					: $format('toast_success_disable_peak_measurement', {
							default: 'Devices have successfully disabled "Peak Power" measurements.',
						});
				break;
			case 'pin_lock':
				successMessage = variable
					? $format('enable_pin_lock_scc_toast', {
							default: 'PIN lock was enabled.',
						})
					: $format('disable_pin_lock_scc_toast', {
							default: 'PIN lock was disabled.',
						});
				break;
		}
	};

	const submit = async () => {
		let selectedDevices: Device[] = Object.values($update.selectedDevices) as Device[];

		if ($update.type === 'appliance_type') {
			selectedDevices = filterPowerMeasuringDevices(selectedDevices);
			if (selectedDevices.length === 0) {
				toast.open({
					type: 'danger',
					title: $format('toast_no_power_measuring_devices', {
						default: 'None of the selected devices measure power.',
					}),
				});
				return;
			}

			if (!skipDesignationPrompt) {
				const designated = selectedDevices.filter((device) =>
					isDeviceEnergyDesignated(getHomeEnergyDesignationForDevice(device, $home_data)),
				);
				if (designated.length > 0) {
					showClearDesignationModal = true;
					return;
				}
			}
		}

		let res;
		if (
			['exclude_event_log', 'exclude_forgotten_lights', 'measure_peak_power'].includes(
				$update.type,
			)
		) {
			res = await postReq('interface/device/bulk_cloud_options', {
				set: $update.type,
				val: $update.type === 'exclude_forgotten_lights' ? !variable : variable,
				devices: JSON.stringify(selectedDevices.map((device) => device.id)),
			});
			if (res.isok) {
				selectedDevices.forEach((device) => {
					const updatedDevice = {
						...device,
						cloud_options: {
							...device.cloud_options,
							[$update.type]:
								$update.type === 'exclude_forgotten_lights' ? !variable : variable,
						},
						...(device.hasOwnProperty($update.type) && {
							[$update.type]:
								$update.type === 'exclude_forgotten_lights' ? !variable : variable,
						}),
					};
					devices.update(updatedDevice);
				});
			}
		} else if ($update.type === 'ai_device_failure') {
			if ($update.subType === 'manage_ai_notifications') {
				res = await postReq('interface/device/bulk_device_notifications', {
					devices: JSON.stringify(selectedDevices.map((device) => device.id)),
					set: 'ai_device_failure',
					val: variable ? 1 : 0,
					ntype: NotifType.MAIL,
				});
				if (res.isok) {
					res = await postReq('interface/device/bulk_device_notifications', {
						devices: JSON.stringify(selectedDevices.map((device) => device.id)),
						set: 'ai_device_failure',
						val: variable ? 1 : 0,
						ntype: NotifType.PUSH,
					});
					if (res.isok) {
						selectedDevices.forEach((device) => {
							const updatedDevice = {
								...device,
								notif_permissions: {
									...device.notif_permissions,
									ai_device_failure: Array(2).fill(variable ? 1 : 0),
								},
							};
							devices.update(updatedDevice);
						});
					}
				}
			} else if ($update.subType === 'enable_ai_model') {
				//bulk enable AI model
				res = await postReq('interface/device/bulk_cloud_options', {
					set: $update.type,
					val: true,
					devices: JSON.stringify(selectedDevices.map((device) => device.id)),
				});
				if (res.isok) {
					selectedDevices.forEach((device) => {
						const updatedDevice = {
							...device,
							cloud_options: {
								...device.cloud_options,
								ai_device_failure: true,
							},
						};
						devices.update(updatedDevice);
					});
				}
			} else if ($update.subType === 'disable_ai_model') {
				//bulk disable AI model
				res = await eraseOrCancelAIModelBulk(selectedDevices, $update.type);
			}
		} else {
			if ($update.type === 'pin_lock' && !variable && !showPinCode) {
				showPinCode = true;
				return;
			}

			const editedDevices = {};
			selectedDevices.forEach((device) => {
				editedDevices[device.id] =
					$update.type === 'consumption_calculation'
						? {
								no_room_cons: variable.roomConsumption,
								no_account_cons: variable.accountConsumption,
							}
						: $update.type === 'appliance_type'
							? {
									appliance_type: variable.value,
									relay_usage: castUsageStringToRelayUsageParam(variable),
								}
							: { pin_lock: variable };
			});

			res = await postReq(`interface/device/bulk_update`, {
				devices_data: JSON.stringify(editedDevices),
			});
			if (res.isok) {
				selectedDevices.forEach(async (device) => {
					const updatedDevice = {
						...device,
						...($update.type === 'consumption_calculation'
							? {
									no_room_cons: variable.roomConsumption,
									no_account_cons: variable.accountConsumption,
								}
							: $update.type === 'appliance_type'
								? {
										appliance_type: variable.value,
										relay_usage: castUsageStringToRelayUsageParam(variable),
									}
								: { pin_lock: variable }),
					};

					if ($update.type === 'appliance_type' && updatedDevice.relay_usage !== 'light') {
						const internalRes = await postReq('interface/device/set_cloud_options', {
							id: updatedDevice.id,
							set: 'exclude_forgotten_lights',
							val: true,
						});
						if (internalRes.isok) {
							updatedDevice.cloud_options.exclude_forgotten_lights = true;
						}
					}

					devices.update(updatedDevice);
				});
			}
		}

		if (res?.isok) {
			toast.open({
				type: 'success',
				title: successMessage,
			});
		} else {
			toast.open({
				type: 'danger',
				title: errorMessage,
			});
		}

		resetEditUpdateStore();
		areFiltersDisabled = false;
		showModal = false;
		skipDesignationPrompt = false;
		showClearDesignationModal = false;
	};

	const handleClearDesignationConfirm = async (): Promise<void> => {
		waitForResponse = true;
		const designated = filterPowerMeasuringDevices(
			Object.values($update.selectedDevices) as Device[],
		).filter((device) =>
			isDeviceEnergyDesignated(getHomeEnergyDesignationForDevice(device, $home_data)),
		);
		const results = await Promise.all(
			designated.map((device) => removeDeviceFromHomeEnergyDesignations(device.id)),
		);
		if (!results.every(Boolean)) {
			waitForResponse = false;
			toast.open({
				type: 'danger',
				title: $format('error_global', { default: 'Something went wrong!' }),
			});
			return;
		}
		showClearDesignationModal = false;
		skipDesignationPrompt = true;
		waitForResponse = false;
		await submit();
	};
</script>

<Modal
	on:close={() => (showModal = false)}
	{showModal}
	title={$update.type === 'appliance_type'
		? capFirstChar(
				$format('settings_row_energy_category', {
					default: 'Energy category',
				}),
			)
		: capFirstChar($format(`sub_settings_title_${$update.type}`))}
>
	<Container slot="content" padding="0">
		<svelte:component
			this={component}
			{checked}
			{roomConsumption}
			{accountConsumption}
			on:toggler={getToggleVariable}
		/>
	</Container>

	<div slot="footer" class="d-flex justify-content-end">
		<Button
			variant="secondary"
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
			on:click={() => (showModal = false)}
		>
			{$format('button_cancel', { default: 'Cancel' })}
		</Button>
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
			on:click={submit}
			disabled={waitForResponse || ($update.type === 'appliance_type' && !variable) || false}
		>
			{$format('button_save', { default: 'Save' })}
		</Button>
	</div>
</Modal>

{#if showPinCode}
	<PinUnlock bind:showModal={showPinCode} on:unlock={submit} on:cancel={() => (showPinCode = false)} />
{/if}

<Modal
	showModal={showClearDesignationModal}
	title={$format('confirm_clear_energy_designation_title', {
		default: 'Change energy category?',
	})}
	backdropClose={!waitForResponse}
	on:close={() => {
		if (!waitForResponse) showClearDesignationModal = false;
	}}
>
	<svelte:fragment slot="content">
		<Text styleType="body">
			{$format('confirm_clear_energy_designation_batch_message', {
				default:
					'One or more selected devices are designated as home energy meters. Changing their energy category will remove those designations.',
			})}
		</Text>
	</svelte:fragment>
	<div slot="footer" class="d-flex justify-content-end gap-s">
		<ButtonV2
			variant="secondary"
			aria-label={$format('button_cancel', { default: 'Cancel' })}
			disabled={waitForResponse}
			on:click={() => (showClearDesignationModal = false)}
		>
			{$format('button_cancel', { default: 'Cancel' })}
		</ButtonV2>
		<ButtonV2
			aria-label={$format('button_confirm', { default: 'Confirm' })}
			loading={waitForResponse}
			disabled={waitForResponse}
			on:click={handleClearDesignationConfirm}
		>
			{$format('button_confirm', { default: 'Confirm' })}
		</ButtonV2>
	</div>
</Modal>
